/**
 * API Service
 *
 * Central place for all HTTP calls to the JAI backend.
 * Uses axios with automatic token injection (see services/storage.js).
 *
 * Usage:
 *   import api from './services/api'
 *   const data = await api.get('/leagues')
 *   const data = await api.post('/auth/login', { email, password })
 */

import axios from 'axios'
import { trackEvent } from '../analytics'
import { readSync, clearSync } from './storage'
import { IS_NATIVE_APP } from './runtime'

/**
 * Where the API actually lives.
 *
 * On web, Express serves the built frontend and the API from the same
 * Render service, so '/api' is same-origin — no absolute URL needed, and
 * this returns '' so every caller's existing '/api/...' path is untouched.
 *
 * Inside a Capacitor app the page is served from capacitor://localhost, which
 * has no backend of its own, so every request needs to be pointed at the real
 * backend explicitly via VITE_API_BASE_URL (baked in at build time, same
 * convention as VITE_POSTHOG_KEY / VITE_SENTRY_DSN — see analytics.js /
 * instrument.js).
 *
 * A missing VITE_API_BASE_URL on a native build does NOT silently fall back
 * to '/api' — that would resolve against capacitor://localhost, and every
 * request would sit there looking like it's loading forever with no visible
 * error, which is the single worst thing to have to debug on a device. It
 * fails loudly instead: a console error naming the problem, and a base URL
 * that always fails fast (an .invalid TLD — reserved by RFC 2606 to never
 * resolve) so the failure shows up immediately in the network tab too.
 */
function resolveApiOrigin() {
  if (!IS_NATIVE_APP) return ''

  const configured = import.meta.env.VITE_API_BASE_URL
  if (!configured) {
    console.error(
      '[api] VITE_API_BASE_URL is not set on a native build. Every API ' +
      'request needs an absolute URL here — there is no same-origin backend ' +
      'inside a Capacitor app. Set VITE_API_BASE_URL at build time.'
    )
    return 'https://vite-api-base-url-not-set.invalid'
  }
  return configured.replace(/\/$/, '')
}

const API_ORIGIN = resolveApiOrigin()

/**
 * Backstop deadline for every request. Axios defaults to NO timeout, so a
 * request the server never answers leaves whatever page fired it spinning
 * forever with no error — the Strategy Establishment hang this was added for.
 *
 * Deliberately longer than any server-side AI deadline (see
 * DEFAULT_TIMEOUT_MS in backend/src/services/claudeService.js), so for a slow
 * AI call the server wins the race and sends its own specific message. This
 * only fires when the server can't answer at all — a dropped connection, a
 * crashed process — where no server message is ever coming.
 *
 * Streaming endpoints are unaffected: they use raw fetch(), not this client.
 */
const REQUEST_TIMEOUT_MS = 4 * 60 * 1000

const api = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  headers: {
    'Content-Type': 'application/json',
    // Lets the backend tell a native request from a web one (see
    // authMiddleware.js's markNativeAppUsage) — there's no way for a
    // website to ask a phone "is this app installed?" directly, so instead
    // the server just remembers when an account has used the app at all.
    // Omitted entirely on web rather than sent as 'false' — the backend
    // only checks whether the header is present.
    ...(IS_NATIVE_APP ? { 'X-Native-App': 'true' } : {}),
  },
  timeout: REQUEST_TIMEOUT_MS,
})

// Attach token to every request automatically. Reads the in-memory sync
// cache (see services/storage.js) rather than localStorage directly — on
// native, localStorage isn't where the token lives at all.
api.interceptors.request.use((config) => {
  const token = readSync('jai_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Login/signup returning 401 just means "wrong credentials" — that's a normal
// rejection for the form on screen to show, not a dead session. Without this
// exclusion, a mistyped password hit the same hard redirect below and wiped
// the login page back to blank before the "Login failed" message could ever
// render, making a wrong password look like the sign-in button did nothing.
// '/auth/refresh' is here for a different reason than the other two: a failed
// refresh is handled deliberately by AuthContext on startup (it decides
// between starting a fresh guest session and sending a real user to sign in).
// Without the exclusion, the hard redirect below would fire first and take
// that decision away.
const AUTH_ENDPOINTS = ['/auth/login', '/auth/signup', '/auth/refresh']

/**
 * Switch that parks the hard redirect below while AuthContext is deciding for
 * itself what a dead session means (see its startup effect).
 *
 * There must be exactly ONE owner of that decision at a time. On app load,
 * several requests go out at once holding the same saved token — the token
 * exchange, the plan status, the referral perks — so a dead token produces a
 * burst of 401s. Whichever one this interceptor saw first used to win, and it
 * always ruled "go to /login". That silently overrode AuthContext's recovery
 * for guests, who have no password and no email and so cannot use the login
 * page at all: they'd be stranded on a screen with no way back into the app.
 */
export const authRedirect = { suspended: false }

// ── Guest free-question wall ─────────────────────────────────────────────────
// The backend answers 429 { code: 'GUEST_SIGNUP_REQUIRED' } once a guest has
// used their free AI requests (middleware/actionQuotaMiddleware.js). Every AI
// call in the app — axios or streamFetch — funnels through one of the two
// places below, so this is the single spot that turns that response into the
// mascot sign-up pop-up (components/GuestSignupGate.jsx listens for the event).
// Without it each of the ~13 AI pages would have to learn about the code
// separately, and any one that forgot would show "Something went wrong".
export const GUEST_LIMIT_EVENT = 'jai:guest-limit'
const GUEST_LIMIT_CODE = 'GUEST_SIGNUP_REQUIRED'

// `scope` says WHOSE allowance ran out: 'guest' (this guest used their own free
// questions) or 'network' (the shared pool for this internet connection is
// spent, so a brand-new guest can be refused on their first question). The
// pop-up words the two differently — telling someone who has asked nothing
// that "you've used your free questions" would be untrue.
const announceGuestLimit = (question, scope) => {
  try {
    window.dispatchEvent(new CustomEvent(GUEST_LIMIT_EVENT, {
      detail: { question: question || '', scope: scope === 'network' ? 'network' : 'guest' },
    }))
  } catch {
    // An event that can't fire just means no pop-up; the request is still refused.
  }
}

// ── Out-of-credits wall (signed-in users) ────────────────────────────────────
// The backend answers 429 { code: 'CREDITS_EXHAUSTED' } when a signed-in user
// has no monthly credits or top-up credits left. Same single-funnel idea as the
// guest wall above: this turns that response into one dialog
// (components/CreditsExhaustedGate.jsx) instead of every AI page learning the
// code. The body carries what the dialog needs (upgradeTo, topupUrl); on the
// iOS app the server leaves both out and sends plain text only.
export const CREDITS_EXHAUSTED_EVENT = 'jai:credits-exhausted'
export const CREDITS_CHANGED_EVENT = 'jai:credits-changed'
const CREDITS_EXHAUSTED_CODE = 'CREDITS_EXHAUSTED'

const announceCreditsExhausted = (body) => {
  try {
    window.dispatchEvent(new CustomEvent(CREDITS_EXHAUSTED_EVENT, { detail: body || {} }))
    window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT))
  } catch { /* no dialog is better than a crash; the request is still refused */ }
}

/** Tell the usage bar the balance probably moved (an AI call just finished). */
export const announceCreditsChanged = () => {
  try { window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT)) } catch { /* nothing listening */ }
}

// Handle 401 globally — token expired → send to login
api.interceptors.response.use(
  (response) => {
    // Any successful POST may have been an AI run that spent credits. Cheap to
    // ask: the hook debounces and the status call is a single small query.
    // (Sign-in, token refresh, billing and account calls never spend credits.)
    if (response.config?.method === 'post' && !/\/(auth|billing|account)\//.test(response.config?.url || '')) announceCreditsChanged()
    return response
  },
  (error) => {
    if (error.response?.status === 429 && error.response?.data?.code === CREDITS_EXHAUSTED_CODE) {
      announceCreditsExhausted(error.response.data)
      // Lets a page tell "the dialog is handling this" apart from a real failure.
      error.creditsExhausted = true
      return Promise.reject(error)
    }
    if (error.response?.status === 429 && error.response?.data?.code === GUEST_LIMIT_CODE) {
      announceGuestLimit('', error.response.data.scope)
      // Lets a page tell "the pop-up is handling this" apart from a real failure.
      error.guestLimit = true
      return Promise.reject(error)
    }
    const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => error.config?.url?.endsWith(path))
    // Startup is handling it — hand the error back and stay out of the way.
    if (authRedirect.suspended) return Promise.reject(error)
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Dev-only: ?preview=1 skips hard redirect so UI can be verified without auth
      const isPreview = import.meta.env.DEV &&
        new URLSearchParams(window.location.search).get('preview') === '1'
      if (!isPreview) {
        // Where to send them depends on who just lost their session. A guest
        // has no password and no email — the login page is a dead end for
        // them, so they go to the front door and get a fresh guest session.
        // A registered user goes to sign in, exactly as before.
        let wasGuest = false
        try {
          wasGuest = !!JSON.parse(readSync('jai_user') || 'null')?.isGuest
        } catch {
          // Unreadable storage — treat it as a registered user and let them
          // sign in, rather than silently swapping them onto a guest account.
        }
        clearSync('jai_token')
        clearSync('jai_user')
        window.location.href = wasGuest ? '/' : '/login'
      }
    }
    return Promise.reject(error)
  }
)

// ── Auth ────────────────────────────────────────────────────────
export const authApi = {
  // details = { firstName, lastName, username, email, password, acceptedTerms, marketingOptIn }
  signup: (details) =>
    api.post('/auth/signup', details),

  /** Start a guest session — the account a first-time visitor gets so the app
   *  works before they sign up. Same { token, user } shape as login. */
  createGuest: () =>
    api.post('/auth/guest'),

  /** Turn the CURRENT guest session into a real account, keeping the same user
   *  id (and so its leagues and notes). Same body as signup.
   *  → { token, user } with a fresh token. */
  claimAccount: (details) =>
    api.post('/auth/claim', details),

  /** Full profile of the logged-in user — more than login returns (join date,
   *  subscription, terms acceptance). Used by the Settings screen. */
  getMe: () =>
    api.get('/auth/me'),

  checkUsername: (username) =>
    api.get('/auth/check-username', { params: { username } }),

  login: (email, password) =>
    api.post('/auth/login', { email, password }),

  logout: () =>
    api.post('/auth/logout'),

  refreshToken: (token) =>
    api.post('/auth/refresh', { token }),

  verifyEmail: (token) =>
    api.post('/auth/verify-email', { token }),

  requestPasswordReset: (email) =>
    api.post('/auth/request-reset', { email }),

  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, newPassword }),

  // ── Yahoo OAuth ────────────────────────────────────────────────
  getYahooStatus: () =>
    api.get('/auth/yahoo/status'),

  startYahooOAuth: () =>
    api.get('/auth/yahoo/start'),

  getYahooLeagues: (pendingCode) =>
    api.get(`/auth/yahoo/leagues`, { params: { code: pendingCode } }),

  // ── ESPN connect (browser extension) ─────────────────────────────
  startEspnConnect: () =>
    api.post('/auth/espn/start'),

  getEspnConnectStatus: (token) =>
    api.get(`/auth/espn/status/${token}`),

  // Native only — see components/NativeEspnHandoff.jsx. Emails the
  // signed-in user a link to finish connecting ESPN on a desktop.
  sendEspnHandoffEmail: () =>
    api.post('/auth/espn/handoff-email'),
}

// ── Account (Settings) ──────────────────────────────────────────
// Everything the user can CHANGE about their own account. authApi above is
// about getting in and out; this is about managing what's inside.
export const accountApi = {
  /** fields = { firstName?, lastName?, username? } — send only what changed. */
  updateProfile: (fields) =>
    api.patch('/account/profile', fields),

  /** Signs out every OTHER device and returns a fresh token for this one. */
  changePassword: (currentPassword, newPassword) =>
    api.post('/account/password', { currentPassword, newPassword }),

  /** Starts an email change. The address does NOT change until the link sent
   *  to the new inbox is clicked. → { pendingEmail } */
  requestEmailChange: (newEmail, password) =>
    api.post('/account/email', { newEmail, password }),

  cancelEmailChange: () =>
    api.post('/account/email/cancel'),

  /** Public — the link lands here from whatever mail app they're using. */
  confirmEmailChange: (token) =>
    api.post('/account/email/confirm', { token }),

  /** prefs = { timezone?, defaultSport?, defaultLeagueId?, adviceStyle?,
   *            adviceDetail?, adviceNotes? } */
  updatePreferences: (prefs) =>
    api.patch('/account/preferences', prefs),

  /** Remember that this user closed the in-app upgrade banner.
   *  variant: 'starter' | 'pro'. Display-only; see UpgradeBanner.jsx. */
  dismissUpgradeBanner: (variant) =>
    api.post('/account/upgrade-banner/dismiss', { variant }),

  /** Returns a fresh token so the current device stays signed in. */
  logoutEverywhere: () =>
    api.post('/account/logout-everywhere'),

  /** Everything held on the server, as JSON. Browser-only data (My Notes)
   *  is folded in by the caller — the server never had it. */
  exportData: () =>
    api.get('/account/export'),

  /** Cancels any subscription, then deletes the account for good. */
  deleteAccount: (password) =>
    api.delete('/account', { data: { password, confirm: 'DELETE' } }),
}

// ── Waitlist (public — no auth) ──────────────────────────────────
export const waitlistApi = {
  join: (name, email, source, ref, marketingOptIn = false) =>
    api.post('/waitlist', { name, email, source, ref, marketingOptIn }),

  getCount: (ref) =>
    api.get('/waitlist/count', { params: ref ? { ref } : {} }),

  /** "Founding Manager" ladder progress for a referral code —
   *  { referralCount, unlockedTools, foundingManager, founderPricing,
   *    currentTier, nextTier }. */
  getReferralStatus: (ref) =>
    api.get('/waitlist/referral-status', { params: { ref } }),
}

// ── Referrals (logged-in user's Founding Manager status) ─────────
export const referralsApi = {
  /** Same shape as waitlistApi.getReferralStatus, matched to the logged-in
   *  user by their account email. Empty perks (not an error) if they never
   *  joined the waitlist. */
  getMine: () =>
    api.get('/referrals/me'),
}

// ── Leagues ─────────────────────────────────────────────────────
export const leaguesApi = {
  /** params: { includeInactive: true } — also returns plan-deactivated leagues
   *  (used by the "choose which league(s) to keep" screen). */
  getAll: (params = {}) =>
    api.get('/leagues', { params }),

  get: (id) =>
    api.get(`/leagues/${id}`),

  create: (data) =>
    api.post('/leagues', data),

  update: (id, data) =>
    api.put(`/leagues/${id}`, data),

  remove: (id) =>
    api.delete(`/leagues/${id}`),

  /** Stops a league counting against the plan's league limit — never deletes it. */
  deactivate: (id) =>
    api.post(`/leagues/${id}/deactivate`),

  /** Re-enables a deactivated league — rejected if it would exceed the plan's limit. */
  reactivate: (id) =>
    api.post(`/leagues/${id}/reactivate`),

  sync: (id) =>
    api.post(`/leagues/${id}/sync`),

  getRoster: (id) =>
    api.get(`/leagues/${id}/roster`),

  /** One rostered player, deep-dived — real stat windows, a real verdict, a
   *  conditional CTA. Single JSON object, not streamed (Analysers rebuild,
   *  Stage 3). Cached server-side 6h per player; pass `refresh: true` to
   *  force regeneration now. */
  analysePlayer: (id, playerName, { refresh = false } = {}) =>
    api.get(`/leagues/${id}/player-analyse`, { params: { player: playerName, ...(refresh ? { refresh: 1 } : {}) } }),

  /** Full team breakdown — real standings, championship odds (titleOddsService,
   *  null when unsimulable), code-selected top performers / trade candidates /
   *  drop candidates, and JAI's comparative strengths/weaknesses. Single JSON
   *  object (Analysers rebuild, Stage 4). Cached server-side weekly; pass
   *  `refresh: true` to force regeneration now. */
  analyseTeam: (id, { refresh = false } = {}) =>
    api.get(`/leagues/${id}/team-analyse`, { params: refresh ? { refresh: 1 } : {} }),

  getPlayers: (id, params = {}) =>
    api.get(`/leagues/${id}/players`, { params }),

  getDraftPlayers: (id) =>
    api.get(`/leagues/${id}/draft/players`),

  getLiveDraft: (id) =>
    api.get(`/leagues/${id}/draft/live`),

  /** Every manager/team in the league, with `isMine` flagged — used to pick a trade partner. */
  getTeams: (id) =>
    api.get(`/leagues/${id}/teams`),

  /** One specific manager's real, enriched roster (not the whole player pool). */
  getTeamRoster: (id, teamId) =>
    api.get(`/leagues/${id}/teams/${teamId}/roster`),

  /** Single JSON response — verdict/acceptanceScore/impacts are computed
   *  server-side in code, not streamed prose. See tradeMathService.js. */
  analyseTrade: (id, giving, receiving, targetTeamId) =>
    api.post(`/leagues/${id}/trade/analyse`, { giving, receiving, targetTeamId }),

  /** Powers "Let JAI alter the trade" / "generate a trade" / "request a new
   *  trade" — reuses seedGiving/seedReceiving where sensible, skips any
   *  package already in `exclude`. Same response shape as analyseTrade,
   *  plus `reasons`. */
  generateTrade: (id, { targetTeamId, seedGiving = [], seedReceiving = [], exclude = [] } = {}) =>
    api.post(`/leagues/${id}/trade/generate`, { targetTeamId, seedGiving, seedReceiving, exclude }),

  /** Real trade suggestions generated from the user's own league — cached
   *  server-side, regenerating weekly or when the roster changes. Pass
   *  `refresh: true` to force regeneration now. */
  getTradeRadar: (id, { refresh = false } = {}) =>
    api.get(`/leagues/${id}/trade/radar`, { params: refresh ? { refresh: 1 } : {} }),

  /** What the filter bar's dropdowns should contain — sport-aware, and for
   *  NBA, the league's own H2H-categories rather than a hardcoded standard 9. */
  getWaiverFilters: (id) =>
    api.get(`/leagues/${id}/waiver/filters`),

  /** Filter-driven waiver recommendations. `filters`: { topFive, positions,
   *  teams, stats, scheduleEase, injuryInsurance }. Cached server-side per
   *  filter signature (30 min) — re-submitting the same combination is free. */
  getWaiverRecommendations: (id, filters = {}) =>
    api.post(`/leagues/${id}/waiver/recommendations`, filters),

  getTeamNews: (id) =>
    api.get(`/leagues/${id}/news/team`),

  getLeagueNews: (id) =>
    api.get(`/leagues/${id}/news/league`),

  /** League Home's ticker — real league transactions merged with sport news.
   *  Unlike getLeagueNews above, this is NOT plan-gated (every plan sees it). */
  getTicker: (id) =>
    api.get(`/leagues/${id}/ticker`),

  /** League Home's stats strip — real record/rank/avg points/form. `matchup`
   *  and `jaiGrade` come back null until the projection model exists (a
   *  later phase); the UI already hides those cells when absent. */
  getMatchup: (id) =>
    api.get(`/leagues/${id}/matchup`),

  /** "This Week's Moves" — real signals detected from the league, ranked
   *  and written up server-side, cached per league per week. Like
   *  getTicker above, NOT plan-gated (every plan sees the section; the
   *  backend drops individual moves the user's plan can't reach). Pass
   *  `refresh: true` to force regeneration. */
  getMoves: (id, { refresh = false } = {}) =>
    api.get(`/leagues/${id}/moves`, { params: refresh ? { refresh: 1 } : {} }),

  /** This week's real opponent — roster, win probability (same number
   *  League Home's matchup card shows), category strength (H2H-categories
   *  NBA leagues only), key threats, and JAI's game plan. Cached
   *  server-side (weekly for the write-up, 3h for the numbers). Pass
   *  `refresh: true` to force regeneration now. */
  getOpponentScout: (id, { refresh = false } = {}) =>
    api.get(`/leagues/${id}/opponent-scout`, { params: refresh ? { refresh: 1 } : {} }),

  /** Real risk score/band/percentile per roster player (computeRisk — the
   *  same primitive the Waiver Wire uses), the team's Injury Grade, and
   *  JAI's written history/currentRisk/jaiView + one validated handcuff pick
   *  per red/orange player. Numeric layer cached 3h server-side, the
   *  written layer once per calendar day. Pass `refresh: true` to bypass both. */
  getInjuryInsurance: (id, { refresh = false } = {}) =>
    api.get(`/leagues/${id}/injury-insurance`, { params: refresh ? { refresh: 1 } : {} }),

  /** NBA (volume) or NFL (matchup) weekly grid — real game counts/opponents/
   *  defence ranks per column, code-computed. `note`/streamer reasons/
   *  `insight` are one validated Claude call, cached server-side per ISO
   *  week. Pass `refresh: true` to force regeneration now. */
  getScheduleSurgeon: (id, { refresh = false } = {}) =>
    api.get(`/leagues/${id}/schedule-surgeon`, { params: refresh ? { refresh: 1 } : {} }),

  /** My Notes (Stage 5) — server-side now, not localStorage. → { notes, limit, used } */
  getNotes: (id) =>
    api.get(`/leagues/${id}/notes`),

  /** 201 { note } | 409 { error, limit } at the per-league cap. `data`:
   *  { title, body, priority?, source?, pinned?, createdAt? } — createdAt is
   *  only meaningful for the one-time local-notes migration (preserves the
   *  original date); omit it for a normal new note. */
  createNote: (id, data) =>
    api.post(`/leagues/${id}/notes`, data),

  getNote: (id, noteId) =>
    api.get(`/leagues/${id}/notes/${noteId}`),

  updateNote: (id, noteId, data) =>
    api.put(`/leagues/${id}/notes/${noteId}`, data),

  deleteNote: (id, noteId) =>
    api.delete(`/leagues/${id}/notes/${noteId}`),

  /** Which platforms JAI currently holds credentials for (Settings →
   *  Connections). Never returns the credentials themselves.
   *  → { connections: [{ platform, connected, leagueCount, ... }] } */
  getConnections: () =>
    api.get('/leagues/connections'),

  /** Forgets stored credentials for one platform across all the user's
   *  leagues. The leagues stay; they just stop syncing. */
  disconnectPlatform: (platform) =>
    api.post(`/leagues/disconnect/${platform}`),
}

// ── Draft Board — Strategy Establishment (Stage 1) ────────────────────────────

/**
 * Tightens REQUEST_TIMEOUT_MS for the strategy calls specifically, to sit 30s
 * past the server's own STRATEGY_TIMEOUT_MS (see
 * backend/src/controllers/draftBoardController.js) rather than a full minute
 * past it. Same ordering rule as the global one: stay LONGER than the server's
 * deadline so its specific message wins the race, and keep it that way if
 * either value is ever retuned.
 */
const STRATEGY_TIMEOUT_MS = 3.5 * 60 * 1000

export const draftBoardApi = {
  /** Which of the 4 Strategy Establishment states to render, plus the
   *  confirmed-or-derived context and the locked strategy summary if any. */
  getContext: (id) =>
    api.get(`/leagues/${id}/draft/context`),

  /** Live pick-list recompute while editing the confirm-details modal —
   *  calls the same backend function draftMathService uses, never a
   *  frontend copy of the math. */
  previewContext: (id, fields) =>
    api.post(`/leagues/${id}/draft/context/preview`, fields),

  confirmContext: (id, fields) =>
    api.post(`/leagues/${id}/draft/context/confirm`, fields),

  /** The user's draft pick list, the trades the platform reports, and the ones
   *  the user told JAI about (see PickTradesDialog). */
  getPickTrades: (id) =>
    api.get(`/leagues/${id}/draft/pick-trades`),

  /** Replaces the user's own pick trades. The server re-plans every strategy
   *  around the new pick list (no AI call, no rebuild). */
  savePickTrades: (id, trades) =>
    api.put(`/leagues/${id}/draft/pick-trades`, trades),

  getStrategies: (id) =>
    api.get(`/leagues/${id}/draft/strategies`),

  /** The expensive call — generates PLAN_STRATEGY_COUNT strategies. */
  generateStrategies: (id) =>
    api.post(`/leagues/${id}/draft/strategies`, undefined, { timeout: STRATEGY_TIMEOUT_MS }),

  getStrategy: (id, sid) =>
    api.get(`/leagues/${id}/draft/strategies/${sid}`),

  customStrategy: (id, prompt) =>
    api.post(`/leagues/${id}/draft/strategies/custom`, { prompt }, { timeout: STRATEGY_TIMEOUT_MS }),

  lockStrategy: (id, sid) =>
    api.post(`/leagues/${id}/draft/strategies/${sid}/lock`),

  /** "Change strategy" — supersedes the locked row. */
  unlockStrategy: (id, sid) =>
    api.post(`/leagues/${id}/draft/strategies/${sid}/unlock`),

  refreshStrategy: (id, sid) =>
    api.post(`/leagues/${id}/draft/strategies/${sid}/refresh`),

  /** State C — analyses a completed draft's real picks into a named strategy. */
  reverseStrategy: (id) =>
    api.post(`/leagues/${id}/draft/strategies/reverse`, undefined, { timeout: STRATEGY_TIMEOUT_MS }),

  // ── Live Draft (Stage 2) ──
  /** Polled every ~10s by useDraftPoll — signal lets the hook abort a
   *  request that's superseded (tab hidden/visible again, unmount). */
  getLiveDraft: (id, signal) =>
    api.get(`/leagues/${id}/draft/live`, { signal }),

  /** The one Claude call in Live Draft — once per pick, cached server-side
   *  by overallPick, so calling it again for the same pick is free. */
  getDraftAdvice: (id, overallPick, targets) =>
    api.post(`/leagues/${id}/draft/live/advice`, { overallPick, targets }),

  /** Starter only — records a manual "Already Selected" exclusion and/or
   *  advances to the next pick. Never touches the platform. */
  // `unmark` puts a player back, `back` goes to the previous round (Starter undo).
  simulateDraftPick: (id, { playerName, advance, unmark, back } = {}) =>
    api.post(`/leagues/${id}/draft/live/simulate`, { playerName, advance, unmark, back }),

  /** ESPN only — hands over the picks the JAI browser extension has read from the
   *  user's ESPN draft room. The full ordered list every time: [{ teamId, playerId }]. */
  pushLivePicks: (id, picks) =>
    api.post(`/leagues/${id}/draft/live/push`, { picks }),

  /** The manual tracker's search source: every draftable player as
   *  { name, position, team, adp }, in ADP order, at most 400. Public ranking
   *  data — no round cap, no AI call. Loaded once when the tracker mounts. */
  getDraftPool: (id) =>
    api.get(`/leagues/${id}/draft/pool`),

  // ── Draft Review (Stage 3) ──
  /** Generates the review on first call (one Claude call), then serves the
   *  cached copy on every reload — never re-bills Claude on GET. */
  getDraftReview: (id) =>
    api.get(`/leagues/${id}/draft/review`),

  /** Explicit regenerate — the only way to re-bill Claude for this tab. */
  refreshDraftReview: (id) =>
    api.post(`/leagues/${id}/draft/review/refresh`),
}

// ── Players ─────────────────────────────────────────────────────────────────
export const playersApi = {
  /** All current roster players. Params: { sport: 'nba'|'nfl', q, position, team, limit } */
  getAll: (params = {}) =>
    api.get('/players', { params }),

  /** Single player full profile + stats. Params: { sport: 'nba'|'nfl' } */
  get: (playerId, params = {}) =>
    api.get(`/players/${playerId}`, { params }),

  /** Batch of player names -> ESPN headshot urls. Body: { sport, names[] } (names capped at 300 server-side) */
  headshots: (sport, names) =>
    api.post('/players/headshots', { sport, names }),
}

// ── Billing (Stripe subscriptions) ──────────────────────────────────────────
// ── Support JAI (optional one-off payment) ───────────────────────
// Public: works logged out. Web only — the /support page is not registered on
// the native app (App.jsx), so nothing on iOS ever calls this.
export const supporterApi = {
  /** → { presets: [10,15,20], min, max, currency } */
  getConfig: () => api.get('/supporter/config'),
  /** amount = whole USD dollars → { url } to redirect to (Stripe Checkout). */
  startCheckout: (amount) => api.post('/supporter/checkout', { amount }),
}

export const billingApi = {
  /** Start a subscription Checkout session → returns { url } to redirect to.
   *  plan: 'pro' | 'elite' | …   billing: 'monthly' | 'annual' (both optional;
   *  omitted falls back to the backend's default price). */
  //  Tracked here rather than at the three call sites (Pricing, and the
  //  post-signup / post-login resume paths) so every route to Stripe is
  //  counted the same way and a fourth one can't quietly miss it.
  startCheckout: (plan, billing) => {
    trackEvent('begin_checkout', { plan, billing: billing || 'default' })
    return api.post('/billing/checkout', { plan, billing })
  },

  /** What changing to this plan would do — changes NOTHING. For someone who
   *  already has a subscription (startCheckout refuses them with 409).
   *  → { action: 'upgrade'|'downgrade'|'trial'|'cancel_scheduled', chargeToday,
   *      creditRemaining, nextChargeAmount, nextChargeAt, effectiveAt, trialEnd,
   *      prorationDate, current, target, currency }   (money in cents, USD) */
  previewPlanChange: (plan, billing) =>
    api.post('/billing/change-plan/preview', { plan, billing }),

  /** Apply the change the user just confirmed. prorationDate is the one the
   *  preview returned, so the charge matches what they were shown.
   *  → { changed: true, action, plan, billing, effectiveAt } — never a url. */
  changePlan: (plan, billing, prorationDate) => {
    trackEvent('change_plan', { plan, billing })
    return api.post('/billing/change-plan', { plan, billing, prorationDate })
  },

  /** Drop a downgrade that is scheduled for the next renewal. */
  cancelScheduledChange: () =>
    api.post('/billing/change-plan/cancel'),

  /** Is the logged-in user subscribed?
   *  → { active, status, plan, currentPeriodEnd, cancelAtPeriodEnd, scheduledChange } */
  getStatus: () =>
    api.get('/billing/status'),

  /** Buy a credit top-up (web only; the server refuses it from the iOS app).
   *  pack: 'topup_100' | 'topup_300' → { url } to redirect to (Stripe Checkout).
   *  Credits are added by the Stripe webhook once the payment completes. */
  startTopup: (pack) => {
    trackEvent('begin_checkout', { plan: 'credits', billing: pack })
    return api.post('/billing/topup/checkout', { pack })
  },

  /** Open Stripe's manage-subscription portal → returns { url } */
  openPortal: () =>
    api.post('/billing/portal'),

  /** Cancel at the end of the period already paid for. Access continues until
   *  then; no further charges are taken.
   *  → { cancelAtPeriodEnd: true, status, accessUntil, wasTrialing } */
  cancelSubscription: () =>
    api.post('/billing/cancel'),

  /** Undo a scheduled cancellation, so the plan renews as normal.
   *  → { cancelAtPeriodEnd: false, status, renewsOn } */
  resumeSubscription: () =>
    api.post('/billing/resume'),
}

// ── Support ("Get help") ────────────────────────────────────────────────────
export const supportApi = {
  /** Email an enquiry to the JAI team. Who it's from comes from the auth
   *  token on the backend, so only the message itself is sent here. */
  sendEnquiry: (subject, message) =>
    api.post('/support', { subject, message }),
}

// Push-notification device registration (native only — see
// services/pushNotifications.js, which is the only caller of these).
export const devicesApi = {
  register: (token, appVersion) =>
    api.post('/devices/register', { token, appVersion }),
  unregister: (token) =>
    api.delete('/devices/register', { data: { token } }),
}

// ── Streaming fetch helper ──────────────────────────────────────────────────
// SSE / ReadableStream endpoints can't use axios (it buffers the full response).
// Use streamFetch for any endpoint that returns a streaming body.
// Auth token injection is automatic and uses the same key as the axios interceptor,
// so if the auth mechanism ever changes it only needs updating in one place.
//
// Callers pass a same-origin-style path ('/api/chat'), same as always — this
// does NOT go through the axios instance, so it doesn't pick up its baseURL.
// API_ORIGIN is '' on web (path used exactly as given, unchanged behaviour)
// and the absolute production URL on native (see resolveApiOrigin above).
//
// Guest wall: a 429 GUEST_SIGNUP_REQUIRED is announced here (pop-up) and the
// returned Response is flagged `guestLimitHit = true`, so the caller can quietly
// put the user's typed question back instead of showing a generic error. The
// blocked question is read from the request's own `messages` array — both chat
// endpoints send one — so no page has to pass it in.
export const streamFetch = async (url, options = {}) => {
  const token = readSync('jai_token')
  const { headers, ...rest } = options
  const response = await fetch(`${API_ORIGIN}${url}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  })

  if (response.status === 429) {
    try {
      // clone(): reading the body here must not use it up for the caller.
      const body = await response.clone().json()
      if (body?.code === CREDITS_EXHAUSTED_CODE) {
        response.creditsExhausted = true
        announceCreditsExhausted(body)
      }
      if (body?.code === GUEST_LIMIT_CODE) {
        let question = ''
        try {
          const sent = JSON.parse(options.body || '{}')
          const last = Array.isArray(sent.messages) ? sent.messages[sent.messages.length - 1] : null
          if (last?.role === 'user' && typeof last.content === 'string') question = last.content
        } catch { /* body wasn't JSON — no question to save */ }
        response.guestLimitHit = true
        announceGuestLimit(question, body.scope)
      }
    } catch { /* not JSON (e.g. a proxy's 429 page) — treat as an ordinary failure */ }
  }
  // A streamed answer is charged when it ends, after this function has already
  // returned. Reading a clone to its end tells the usage bar to refresh then,
  // without touching the body the caller is reading.
  if (response.ok && response.body) {
    response.clone().arrayBuffer().then(announceCreditsChanged, () => { /* the caller deals with a broken stream */ })
  }
  return response
}

export default api
